import type { Category, Frequency, RecurringTemplate, Transaction } from './model';
import { spendingOf } from './spending';

/**
 * Annualized bills (V1.5): what each recurring bill costs over a year, so
 * a ₺129.99 subscription reads as the ₺1,559.88 it really is. Pure.
 *
 *  - A fixed bill: its amount × its periods a year (12, 4 or 1).
 *  - A variable bill: the average of its last recordings (up to three) ×
 *    periods a year, marked as an estimate; never recorded → no amount.
 *  - Only spending counts: a transfer to savings or an already-tracked card
 *    bill is not a cost (the spending rule, as everywhere).
 *  - Subscriptions are the bills you could cancel: those in a
 *    discretionary category (streaming, gaming, shopping). Everything else
 *    is an essential (rent, utilities, loans).
 *  - Paused bills are totalled apart: what pausing saves a year.
 */
export const PERIODS_PER_YEAR: Record<Frequency, number> = { monthly: 12, quarterly: 4, yearly: 1 };

export interface AnnualBill {
  template: RecurringTemplate;
  /** What one period costs (the fixed amount, or the recent average); null when unknown. */
  perPeriod: number | null;
  perYear: number | null;
  /** A variable bill's amount is an average of its recordings. */
  estimate: boolean;
  subscription: boolean;
}

export interface AnnualSummary {
  /** Active bills that cost money, most expensive a year first (unknown amounts last). */
  bills: AnnualBill[];
  total: number;
  subscriptions: { total: number; count: number };
  essentials: { total: number; count: number };
  /** Active bills with no amount to go on yet. */
  unknown: number;
  paused: { total: number; count: number };
}

const RECENT = 3;

export function annualize(
  templates: readonly RecurringTemplate[],
  linked: readonly Transaction[],
  categories: ReadonlyMap<string, Pick<Category, 'nature'>>,
): AnnualSummary {
  const history = new Map<string, Transaction[]>();
  for (const e of linked) {
    if (!e.recurringTemplateId) continue;
    const list = history.get(e.recurringTemplateId) ?? [];
    list.push(e);
    history.set(e.recurringTemplateId, list);
  }
  const all: (AnnualBill & { active: boolean })[] = [];
  for (const template of templates) {
    // A bill that is not spending (a transfer to savings, a card bill already tracked) is not a cost.
    if (spendingOf({ type: template.type, countsAsSpending: template.countsAsSpending, amountMinor: 1 }) <= 0) continue;
    let perPeriod: number | null = null;
    let estimate = false;
    if (template.kind === 'fixed' && template.amountMinor !== null) {
      perPeriod = template.amountMinor;
    } else {
      const recent = (history.get(template.id) ?? [])
        .slice()
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
        .slice(0, RECENT);
      if (recent.length > 0) {
        perPeriod = Math.round(recent.reduce((s, e) => s + Math.max(0, spendingOf(e)), 0) / recent.length);
        estimate = true;
      } else if (template.amountMinor !== null) {
        // A variable bill's own estimate, until it has been recorded.
        perPeriod = template.amountMinor;
        estimate = true;
      }
    }
    all.push({
      template,
      perPeriod,
      perYear: perPeriod === null ? null : perPeriod * PERIODS_PER_YEAR[template.frequency],
      estimate,
      subscription: categories.get(template.categoryId)?.nature === 'discretionary',
      active: template.active,
    });
  }

  const active = all.filter((b) => b.active);
  const sum = (list: typeof all) => list.reduce((s, b) => s + (b.perYear ?? 0), 0);
  const known = (list: typeof all) => list.filter((b) => b.perYear !== null);
  const subs = known(active.filter((b) => b.subscription));
  const essentials = known(active.filter((b) => !b.subscription));
  const paused = known(all.filter((b) => !b.active));
  return {
    bills: active.map(({ active: _a, ...b }) => b).sort((a, b) => (b.perYear ?? -1) - (a.perYear ?? -1) || a.template.name.localeCompare(b.template.name)),
    total: sum(active),
    subscriptions: { total: sum(subs), count: subs.length },
    essentials: { total: sum(essentials), count: essentials.length },
    unknown: active.length - known(active).length,
    paused: { total: sum(paused), count: paused.length },
  };
}
